import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { encodeRle, decodeRle, PIXEL_ART_MAX_DIM, COLOR_HEX, type CanvasElement, type PixelArt } from "../../domain/canvas-model";
import { pixelize, fitGridToAspect } from "../../domain/pixelize";
import { useI18n } from "../../i18n";

type Tool = "PENCIL" | "ERASER" | "FILL" | "EYEDROPPER";
type FitMode = "CONTAIN" | "COVER";

function hexA(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, "0");
  return `${hex}${a === "ff" ? "" : a}`;
}

const COLOR_OPTS = [8, 16, 32, 64];
const LONGSIDE_OPTS = [32, 64, 128, 256];
// The standard "base palette we have" — the canonical Grid colour tokens. Always
// available to paint with, kept visually distinct from an imported image palette.
const BASE_PALETTE: string[] = Array.from(new Set(Object.values(COLOR_HEX)));

/** Docked Pixel Studio — drawing editor + Import→Pixelize (1 cell == 1 parcel unit). */
export function PixelStudio({ el, onPatch, onPatchCommit, onClose, onDelete, onSaveCanvas }: {
  el: CanvasElement; onPatch: (id: string, p: Partial<CanvasElement>) => void;
  onPatchCommit: (id: string, p: Partial<CanvasElement>) => void; onClose: () => void; onDelete: () => void;
  onSaveCanvas: () => Promise<void> | void;
}) {
  const { t } = useI18n();
  const pa = el.pixel_art as PixelArt;
  const [w, setW] = useState(pa.w);
  const [h, setH] = useState(pa.h);
  const cellsRef = useRef<Uint16Array>(decodeRle(pa.rle, pa.w, pa.h));
  const [palette, setPalette] = useState<string[]>(() => [...pa.palette]);
  const [colorIdx, setColorIdx] = useState(0); // 0-based into palette
  const [tool, setTool] = useState<Tool>("PENCIL");
  const [brush, setBrush] = useState(1);
  const [cellPx, setCellPx] = useState(() => Math.max(4, Math.min(28, Math.floor(560 / Math.max(pa.w, pa.h)))));
  const [gridOn, setGridOn] = useState(true);
  const [hex, setHex] = useState("#22d3ee");
  const [alpha, setAlpha] = useState(1);
  const [painted, setPainted] = useState(0);
  const [showStart, setShowStart] = useState(false);
  const undo = useRef<{ w: number; h: number; palette: string[]; cells: Uint16Array }[]>([]);
  const redo = useRef<{ w: number; h: number; palette: string[]; cells: Uint16Array }[]>([]);
  const cv = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  // Snapshot of the artwork as it was when the studio opened — used to revert on
  // "Chiudi senza salvare". `dirty` = there are pixel edits not yet persisted to
  // the server (i.e. the canvas has not been Saved since the last change).
  const origRef = useRef<PixelArt>({ w: pa.w, h: pa.h, palette: [...pa.palette], rle: pa.rle });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  // ── Import → Pixelize state ─────────────────────────────────────────────
  const [importSrc, setImportSrc] = useState<HTMLImageElement | null>(null);
  const [fit, setFit] = useState<FitMode>("CONTAIN");
  const [maxColors, setMaxColors] = useState(32);
  const [dither, setDither] = useState(true);
  const [alphaThresh, setAlphaThresh] = useState(16);
  const [longSide, setLongSide] = useState(64);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);

  const draw = () => {
    const c = cv.current; if (!c) return;
    const ctx = c.getContext("2d"); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const cells = cellsRef.current;
    let count = 0;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const v = cells[y * w + x];
      if (v === 0) {
        ctx.fillStyle = ((x + y) % 2) ? "#0e1420" : "#141b28";
        ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx);
      } else {
        count++;
        ctx.fillStyle = palette[v - 1] ?? "#000";
        ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx);
      }
    }
    if (gridOn && cellPx >= 6) {
      ctx.strokeStyle = "rgba(255,255,255,0.06)"; ctx.lineWidth = 1;
      for (let x = 0; x <= w; x++) { ctx.beginPath(); ctx.moveTo(x * cellPx + 0.5, 0); ctx.lineTo(x * cellPx + 0.5, h * cellPx); ctx.stroke(); }
      for (let y = 0; y <= h; y++) { ctx.beginPath(); ctx.moveTo(0, y * cellPx + 0.5); ctx.lineTo(w * cellPx, y * cellPx + 0.5); ctx.stroke(); }
    }
    setPainted(count);
  };
  useLayoutEffect(draw); // redraw every render (cheap for ≤256²)

  const pushUndo = () => {
    undo.current.push({ w, h, palette: [...palette], cells: cellsRef.current.slice() });
    if (undo.current.length > 60) undo.current.shift();
    redo.current = [];
  };
  const boxFor = (nw: number, nh: number) => ({ ...(el.box ?? { x: 0, y: 0, w: nw, h: nh }), w: nw, h: nh });
  const commitLive = () => { onPatch(el.element_id, { pixel_art: { w, h, palette, rle: encodeRle(cellsRef.current) }, box: boxFor(w, h) }); setDirty(true); setJustSaved(false); };

  const paintCell = (cx: number, cy: number) => {
    const cells = cellsRef.current;
    const val = tool === "ERASER" ? 0 : colorIdx + 1;
    const r = brush - 1;
    for (let dy = -Math.floor(r / 2); dy <= Math.ceil(r / 2); dy++)
      for (let dx = -Math.floor(r / 2); dx <= Math.ceil(r / 2); dx++) {
        const x = cx + dx, y = cy + dy;
        if (x >= 0 && x < w && y >= 0 && y < h) cells[y * w + x] = val;
      }
  };
  const floodFill = (cx: number, cy: number) => {
    const cells = cellsRef.current;
    const target = cells[cy * w + cx];
    const val = colorIdx + 1;
    if (target === val) return;
    const stack = [[cx, cy]];
    while (stack.length) {
      const [x, y] = stack.pop()!;
      if (x < 0 || x >= w || y < 0 || y >= h) continue;
      if (cells[y * w + x] !== target) continue;
      cells[y * w + x] = val;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
  };
  const cellFromEvent = (e: React.PointerEvent) => {
    const c = cv.current!; const rect = c.getBoundingClientRect();
    return [Math.floor((e.clientX - rect.left) / cellPx), Math.floor((e.clientY - rect.top) / cellPx)];
  };
  const onDown = (e: React.PointerEvent) => {
    const [cx, cy] = cellFromEvent(e);
    if (cx < 0 || cx >= w || cy < 0 || cy >= h) return;
    if (tool === "EYEDROPPER") { const v = cellsRef.current[cy * w + cx]; if (v > 0) setColorIdx(v - 1); return; }
    pushUndo();
    if (tool === "FILL") floodFill(cx, cy); else paintCell(cx, cy);
    drawing.current = tool === "PENCIL" || tool === "ERASER";
    draw();
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const [cx, cy] = cellFromEvent(e);
    if (cx < 0 || cx >= w || cy < 0 || cy >= h) return;
    paintCell(cx, cy); draw();
  };
  const onUp = () => { if (drawing.current) { drawing.current = false; commitLive(); } };

  const restore = (s: { w: number; h: number; palette: string[]; cells: Uint16Array }) => {
    setW(s.w); setH(s.h); setPalette(s.palette); cellsRef.current = s.cells;
    onPatch(el.element_id, { pixel_art: { w: s.w, h: s.h, palette: s.palette, rle: encodeRle(s.cells) }, box: boxFor(s.w, s.h) });
    setDirty(true); setJustSaved(false);
  };
  const doUndo = () => { const prev = undo.current.pop(); if (!prev) return; redo.current.push({ w, h, palette: [...palette], cells: cellsRef.current.slice() }); restore(prev); };
  const doRedo = () => { const nxt = redo.current.pop(); if (!nxt) return; undo.current.push({ w, h, palette: [...palette], cells: cellsRef.current.slice() }); restore(nxt); };
  const clearAll = () => { pushUndo(); cellsRef.current = new Uint16Array(w * h); draw(); commitLive(); };
  const addColor = () => {
    if (palette.length >= 256) return;
    const col = hexA(hex, alpha);
    const next = [...palette, col]; setPalette(next); setColorIdx(next.length - 1);
    try { const rec = JSON.parse(localStorage.getItem("pixel_recent") || "[]"); localStorage.setItem("pixel_recent", JSON.stringify([col, ...rec.filter((x: string) => x !== col)].slice(0, 24))); } catch { /* noop */ }
  };
  const recent: string[] = useMemo(() => { try { return JSON.parse(localStorage.getItem("pixel_recent") || "[]"); } catch { return []; } }, [painted]);
  // Pick a colour from the base palette: reuse its index if already present in
  // the active palette, otherwise append it and select it.
  const pickBase = (c: string) => {
    const idx = palette.indexOf(c);
    if (idx >= 0) { setColorIdx(idx); return; }
    const next = [...palette, c]; setPalette(next); setColorIdx(next.length - 1);
  };

  const save = () => onPatchCommit(el.element_id, { pixel_art: { w, h, palette, rle: encodeRle(cellsRef.current) }, box: boxFor(w, h) });
  // Salva = commit del disegno nell'elemento + persistenza dell'intero canvas sul
  // server, così artwork E canvas vengono salvati davvero (non solo in locale).
  const handleSave = async () => {
    if (saving) return;
    save();
    setSaving(true);
    try { await onSaveCanvas(); setDirty(false); setJustSaved(true); }
    catch { /* the canvas Save surfaces its own error modal */ }
    finally { setSaving(false); }
  };
  const requestClose = () => { if (dirty) setConfirmClose(true); else onClose(); };
  const discardAndClose = () => {
    const o = origRef.current;
    onPatchCommit(el.element_id, { pixel_art: { w: o.w, h: o.h, palette: [...o.palette], rle: o.rle }, box: boxFor(o.w, o.h) });
    setConfirmClose(false); onClose();
  };
  const saveAndClose = async () => { await handleSave(); setConfirmClose(false); onClose(); };
  useEffect(() => { const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { if (importSrc) { setImportSrc(null); return; } if (confirmClose) { setConfirmClose(false); return; } requestClose(); } }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); });

  // ── Import → Pixelize logic ──────────────────────────────────────────────
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); const long = Math.max(img.naturalWidth, img.naturalHeight) >= 128 ? 64 : 32; setLongSide(long); setImportSrc(img); };
    img.onerror = () => { URL.revokeObjectURL(url); };
    img.src = url;
  };

  // Rasterise source into a W×H offscreen ImageData honouring the fit mode.
  const rasterize = (img: HTMLImageElement, gw: number, gh: number, mode: FitMode): Uint8ClampedArray | null => {
    const off = document.createElement("canvas"); off.width = gw; off.height = gh;
    const ctx = off.getContext("2d"); if (!ctx) return null;
    ctx.clearRect(0, 0, gw, gh);
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const s = mode === "CONTAIN" ? Math.min(gw / iw, gh / ih) : Math.max(gw / iw, gh / ih);
    const dw = iw * s, dh = ih * s;
    ctx.drawImage(img, (gw - dw) / 2, (gh - dh) / 2, dw, dh);
    return ctx.getImageData(0, 0, gw, gh).data;
  };

  const importResult = useMemo(() => {
    if (!importSrc) return null;
    const { w: gw, h: gh } = fitGridToAspect(importSrc.naturalWidth, importSrc.naturalHeight, longSide, PIXEL_ART_MAX_DIM);
    const data = rasterize(importSrc, gw, gh, fit);
    if (!data) return null;
    const { palette: pal, cells } = pixelize(data, gw, gh, { maxColors, dither, alphaThreshold: alphaThresh });
    return { w: gw, h: gh, palette: pal, cells };
  }, [importSrc, longSide, fit, maxColors, dither, alphaThresh]);

  // Draw the import preview.
  useLayoutEffect(() => {
    const c = previewRef.current; if (!c || !importResult) return;
    const { w: gw, h: gh, palette: pal, cells } = importResult;
    c.width = gw; c.height = gh;
    const ctx = c.getContext("2d"); if (!ctx) return;
    const im = ctx.createImageData(gw, gh);
    for (let i = 0; i < gw * gh; i++) {
      const v = cells[i];
      if (v === 0) { im.data[i * 4 + 3] = 0; continue; }
      const hx = (pal[v - 1] || "#000000").replace("#", "");
      im.data[i * 4] = parseInt(hx.slice(0, 2), 16) || 0;
      im.data[i * 4 + 1] = parseInt(hx.slice(2, 4), 16) || 0;
      im.data[i * 4 + 2] = parseInt(hx.slice(4, 6), 16) || 0;
      im.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(im, 0, 0);
  }, [importResult]);

  const applyImport = () => {
    if (!importResult) return;
    pushUndo();
    const { w: gw, h: gh, palette: pal, cells } = importResult;
    setW(gw); setH(gh); setPalette(pal); cellsRef.current = cells; setColorIdx(0);
    setCellPx(Math.max(4, Math.min(28, Math.floor(560 / Math.max(gw, gh)))));
    onPatch(el.element_id, { pixel_art: { w: gw, h: gh, palette: pal, rle: encodeRle(cells) }, box: boxFor(gw, gh) });
    setDirty(true); setJustSaved(false);
    setImportSrc(null);
    setShowStart(true);
    window.setTimeout(() => setShowStart(false), 6000);
  };

  const T = ({ id, label }: { id: Tool; label: string }) => (
    <button data-testid={`pixel-tool-${id}`} onClick={() => setTool(id)}
      className={`px-2 py-1 rounded-sm border text-[10px] font-mono ${tool === id ? "border-neon-teal text-neon-teal" : "border-border-soft text-text-secondary"}`}>{label}</button>
  );

  return (
    <div data-testid="pixel-studio" className="absolute top-12 bottom-0 right-0 z-50 w-[420px] max-w-[95vw] bg-[#0d1117] border-l border-[#21262d] shadow-2xl flex flex-col">
      <div className="flex items-center justify-between px-3 h-11 border-b border-[#21262d]">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neon-teal">{t("editor.pixel.title")} · {w}×{h}</div>
        <div className="flex gap-1">
          <button data-testid="pixel-delete" onClick={onDelete} title={t("editor.pixel.deleteTitle")}
            className="px-2 py-1 rounded-sm border border-[#ff5a4d]/70 text-[#ff8a80] text-[10px] font-mono hover:bg-[#ff5a4d]/15">{t("editor.pixel.delete")}</button>
          <button data-testid="pixel-close-x" onClick={requestClose} title={t("editor.pixel.closeTitle")}
            className="px-2 py-1 rounded-sm border border-border-soft text-text-secondary text-[10px] font-mono hover:text-text-primary">✕</button>
        </div>
      </div>
      <div className="flex-1 min-h-0 p-3 space-y-3 overflow-y-auto">
        {showStart && (
          <div data-testid="pixel-starting-point" className="rounded-md border border-neon-teal/50 bg-neon-teal-soft/40 p-2.5">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neon-teal mb-0.5">{t("editor.pixel.startingPoint")}</div>
            <div className="text-[11px] text-text-secondary leading-snug">{t("editor.pixel.startingPointBody")}</div>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          <T id="PENCIL" label={`✏ ${t("editor.pixel.pencil")}`} /><T id="ERASER" label={`⌫ ${t("editor.pixel.eraser")}`} /><T id="FILL" label={`🪣 ${t("editor.pixel.fill")}`} /><T id="EYEDROPPER" label={`⛏ ${t("editor.pixel.pick")}`} />
          <button data-testid="pixel-undo" onClick={doUndo} className="px-2 py-1 rounded-sm border border-border-soft text-[10px] font-mono">↶ {t("editor.pixel.undo")}</button>
          <button data-testid="pixel-redo" onClick={doRedo} className="px-2 py-1 rounded-sm border border-border-soft text-[10px] font-mono">↷ {t("editor.pixel.redo")}</button>
          <button data-testid="pixel-clear" onClick={clearAll} className="px-2 py-1 rounded-sm border border-border-soft text-[10px] font-mono text-text-muted">{t("editor.pixel.clear")}</button>
        </div>

        <button data-testid="pixel-import-open" onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-neon-teal/60 text-neon-teal text-[11px] font-mono uppercase tracking-[0.14em] hover:bg-neon-teal-soft">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
          {t("editor.pixel.importOpen")}
        </button>
        <input ref={fileRef} data-testid="pixel-import-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} />

        <div className="flex items-center gap-3 text-[10px] font-mono text-text-muted">
          <label className="flex items-center gap-1">{t("editor.pixel.brush")}
            <select data-testid="pixel-brush" value={brush} onChange={(e) => setBrush(parseInt(e.target.value))} className="bg-obsidian border border-border-soft rounded-sm px-1 py-0.5 text-text-primary">
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}×{n}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1 flex-1">{t("editor.pixel.zoom")}
            <button data-testid="pixel-zoom-out" type="button" title={t("editor.pixel.zoomOutTitle")}
              onClick={() => setCellPx((c) => Math.max(3, c - 2))}
              className="w-5 h-5 flex items-center justify-center rounded-sm border border-border-soft text-text-primary leading-none hover:border-neon-teal hover:text-neon-teal">−</button>
            <input data-testid="pixel-zoom" type="range" min={3} max={32} value={cellPx} onChange={(e) => setCellPx(parseInt(e.target.value))} className="flex-1" />
            <button data-testid="pixel-zoom-in" type="button" title={t("editor.pixel.zoomInTitle")}
              onClick={() => setCellPx((c) => Math.min(32, c + 2))}
              className="w-5 h-5 flex items-center justify-center rounded-sm border border-border-soft text-text-primary leading-none hover:border-neon-teal hover:text-neon-teal">+</button>
          </label>
          <button data-testid="pixel-grid-toggle" onClick={() => setGridOn((g) => !g)} className={`px-2 py-0.5 rounded-sm border text-[10px] ${gridOn ? "border-neon-teal text-neon-teal" : "border-border-soft"}`}>{t("editor.pixel.grid")}</button>
        </div>

        <div data-testid="pixel-canvas-wrap" className="overflow-auto border border-[#21262d] rounded-sm bg-obsidian" style={{ maxHeight: "46vh" }}>
          <canvas ref={cv} width={w * cellPx} height={h * cellPx} data-testid="pixel-draw-canvas"
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
            style={{ display: "block", touchAction: "none", cursor: "crosshair", imageRendering: "pixelated" }} />
        </div>
        <div className="text-[10px] font-mono text-text-muted">{t("editor.pixel.cellsPainted")} <span data-testid="pixel-cell-count" className="text-text-secondary">{painted}</span> / {w * h}</div>

        <div className="space-y-1">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.pixel.paletteActive")} <span className="text-text-secondary normal-case tracking-normal">{t("editor.pixel.paletteActiveNote")}</span></div>
          <div className="flex flex-wrap gap-1" data-testid="pixel-palette">
            {palette.map((c, i) => (
              <button key={i} data-testid={`pixel-swatch-${i}`} onClick={() => setColorIdx(i)} title={c}
                className={`w-6 h-6 rounded-sm border ${colorIdx === i ? "border-neon-teal ring-1 ring-neon-teal" : "border-border-soft"}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input data-testid="pixel-hex" value={hex} onChange={(e) => setHex(e.target.value)} className="w-24 bg-obsidian border border-border-soft rounded-sm px-2 py-1 text-[11px] font-mono text-text-primary" />
            <label className="flex items-center gap-1 flex-1 text-[10px] font-mono text-text-muted">A
              <input data-testid="pixel-alpha" type="range" min={0} max={1} step={0.05} value={alpha} onChange={(e) => setAlpha(parseFloat(e.target.value))} className="flex-1" />
            </label>
            <button data-testid="pixel-add-color" onClick={addColor} className="chip">{t("editor.pixel.add")}</button>
          </div>
          {recent.length > 0 && (<>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.pixel.recent")}</div>
            <div className="flex flex-wrap gap-1">
              {recent.map((c, i) => (
                <button key={i} data-testid={`pixel-recent-${i}`} onClick={() => { const next = [...palette, c]; setPalette(next); setColorIdx(next.length - 1); }} className="w-5 h-5 rounded-sm border border-border-soft" style={{ backgroundColor: c }} />
              ))}
            </div>
          </>)}
        </div>

        <div className="space-y-1 pt-1 border-t border-[#21262d]">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.pixel.paletteBase")} <span className="text-text-secondary normal-case tracking-normal">{t("editor.pixel.paletteBaseNote")}</span></div>
          <div className="flex flex-wrap gap-1" data-testid="pixel-base-palette">
            {BASE_PALETTE.map((c, i) => (
              <button key={c} data-testid={`pixel-base-swatch-${i}`} onClick={() => pickBase(c)} title={c}
                className="w-5 h-5 rounded-sm border border-border-soft hover:ring-1 hover:ring-neon-teal"
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </div>

      {/* Persistent action bar — pinned at the panel bottom so Save / Delete /
          Close are ALWAYS reachable, even if the top header is off-screen. */}
      {/* Persistent action bar — pinned at the panel bottom so Save / Delete /
          Close are ALWAYS reachable, even if the top header is off-screen.
          Salva = persiste artwork + canvas sul server; Chiudi = chiude (con
          conferma se ci sono modifiche non salvate). */}
      <div data-testid="pixel-action-bar" className="shrink-0 flex items-center gap-2 px-3 h-12 border-t border-[#21262d] bg-[#0d1117]">
        <button data-testid="pixel-delete-footer" onClick={onDelete} title={t("editor.pixel.deleteTitle")}
          className="px-2.5 py-1 rounded-sm border border-[#ff5a4d]/70 text-[#ff8a80] text-[10px] font-mono hover:bg-[#ff5a4d]/15">🗑 {t("editor.pixel.delete")}</button>
        <span data-testid="pixel-save-status" className="ml-1 text-[10px] font-mono truncate">
          {dirty ? <span className="text-amber-300">● {t("editor.pixel.unsaved")}</span>
                 : justSaved ? <span className="text-neon-teal">✓ {t("editor.pixel.saved")}</span>
                 : <span className="text-text-muted">—</span>}
        </span>
        <div className="flex-1" />
        <button data-testid="pixel-close-footer" onClick={requestClose}
          className="px-2.5 py-1 rounded-sm border border-border-soft text-text-secondary text-[10px] font-mono hover:text-text-primary">{t("editor.pixel.close")}</button>
        <button data-testid="pixel-save-footer" onClick={() => void handleSave()} disabled={saving}
          className="px-3 py-1 rounded-sm border border-neon-teal bg-neon-teal-soft text-neon-teal text-[10px] font-mono uppercase tracking-[0.12em] hover:bg-neon-teal/20 disabled:opacity-50">
          {saving ? t("editor.pixel.saving") : t("editor.pixel.saveArtworkCanvas")}</button>
      </div>

      {importSrc && importResult && (
        <div data-testid="pixel-import-panel" className="absolute inset-0 z-10 bg-[#0d1117] flex flex-col">
          <div className="flex items-center justify-between px-3 h-11 border-b border-[#21262d]">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neon-teal">{t("editor.pixel.pixelizeTitle")}</div>
            <button data-testid="pixel-import-cancel" onClick={() => setImportSrc(null)} className="chip">{t("editor.pixel.cancel")}</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            <div className="rounded-md border border-[#21262d] bg-obsidian p-3 flex items-center justify-center" style={{ minHeight: 180 }}>
              <canvas ref={previewRef} data-testid="pixel-import-preview"
                style={{ maxWidth: "100%", maxHeight: 260, width: importResult.w >= importResult.h ? "100%" : "auto", height: importResult.h > importResult.w ? 260 : "auto", imageRendering: "pixelated", background: "transparent" }} />
            </div>
            <div className="text-[10px] font-mono text-text-muted">{t("editor.pixel.resultingGrid")} <span className="text-neon-teal">{importResult.w}×{importResult.h}</span> · {t("editor.pixel.colorsLabel")} {importResult.palette.length}</div>

            <label className="block text-[10px] font-mono text-text-muted">{t("editor.pixel.resolution")}
              <div className="flex gap-1 mt-1" data-testid="pixel-import-longside">
                {LONGSIDE_OPTS.map((n) => (
                  <button key={n} data-testid={`pixel-import-longside-${n}`} onClick={() => setLongSide(n)}
                    className={`flex-1 px-2 py-1 rounded-sm border text-[10px] font-mono ${longSide === n ? "border-neon-teal text-neon-teal" : "border-border-soft text-text-secondary"}`}>{n}</button>
                ))}
              </div>
            </label>

            <label className="block text-[10px] font-mono text-text-muted">{t("editor.pixel.fitMode")}
              <div className="flex gap-1 mt-1">
                {(["CONTAIN", "COVER"] as FitMode[]).map((m) => (
                  <button key={m} data-testid={`pixel-import-fit-${m}`} onClick={() => setFit(m)}
                    className={`flex-1 px-2 py-1 rounded-sm border text-[10px] font-mono ${fit === m ? "border-neon-teal text-neon-teal" : "border-border-soft text-text-secondary"}`}>{m === "CONTAIN" ? t("editor.pixel.fitContain") : t("editor.pixel.fitCover")}</button>
                ))}
              </div>
            </label>

            <label className="block text-[10px] font-mono text-text-muted">{t("editor.pixel.maxColors")}
              <div className="flex gap-1 mt-1">
                {COLOR_OPTS.map((n) => (
                  <button key={n} data-testid={`pixel-import-colors-${n}`} onClick={() => setMaxColors(n)}
                    className={`flex-1 px-2 py-1 rounded-sm border text-[10px] font-mono ${maxColors === n ? "border-neon-teal text-neon-teal" : "border-border-soft text-text-secondary"}`}>{n}</button>
                ))}
              </div>
            </label>

            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-text-muted">{t("editor.pixel.dithering")}</span>
              <button data-testid="pixel-import-dither" onClick={() => setDither((d) => !d)}
                className={`px-3 py-1 rounded-sm border text-[10px] font-mono ${dither ? "border-neon-teal text-neon-teal" : "border-border-soft text-text-secondary"}`}>{dither ? t("editor.pixel.on") : t("editor.pixel.off")}</button>
            </div>

            <label className="block text-[10px] font-mono text-text-muted">{t("editor.pixel.alphaThreshold", { v: alphaThresh })}
              <input data-testid="pixel-import-alpha-threshold" type="range" min={0} max={254} value={alphaThresh} onChange={(e) => setAlphaThresh(parseInt(e.target.value))} className="w-full mt-1" />
            </label>
          </div>
          <div className="p-3 border-t border-[#21262d]">
            <button data-testid="pixel-import-apply" onClick={applyImport}
              className="w-full px-3 py-2 rounded-md border border-neon-teal bg-neon-teal-soft text-neon-teal text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-neon-teal/20">
              {t("editor.pixel.useAsBase")}
            </button>
          </div>
        </div>
      )}

      {confirmClose && (
        <div data-testid="pixel-confirm-close" className="absolute inset-0 z-20 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-[320px] rounded-md border border-[#21262d] bg-[#0d1117] p-4">
            <div className="font-mono text-[12px] text-text-primary">{t("editor.pixel.unsavedTitle")}</div>
            <p className="mt-1.5 text-[11px] text-text-muted leading-relaxed">{t("editor.pixel.unsavedBody")}</p>
            <div className="mt-3 flex flex-col gap-2">
              <button data-testid="pixel-confirm-save" onClick={() => void saveAndClose()} disabled={saving}
                className="px-3 py-1.5 rounded-sm border border-neon-teal bg-neon-teal-soft text-neon-teal text-[11px] font-mono uppercase tracking-[0.1em] hover:bg-neon-teal/20 disabled:opacity-50">{saving ? t("editor.pixel.saving") : t("editor.pixel.saveAndClose")}</button>
              <button data-testid="pixel-confirm-discard" onClick={discardAndClose}
                className="px-3 py-1.5 rounded-sm border border-[#ff5a4d]/70 text-[#ff8a80] text-[11px] font-mono hover:bg-[#ff5a4d]/15">{t("editor.pixel.discardClose")}</button>
              <button data-testid="pixel-confirm-cancel" onClick={() => setConfirmClose(false)}
                className="px-3 py-1.5 rounded-sm border border-border-soft text-text-secondary text-[11px] font-mono hover:text-text-primary">{t("editor.pixel.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
